import React, { useContext, useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import clsx from "clsx";
import { isBlank, notBlank } from "../../../util/ext";
import { equals, filter, last, sort, test, toUpper } from "ramda";
import { byRecentlyUpdated, childrenHooks } from "../organizeUtil";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleRight } from "@fortawesome/pro-regular-svg-icons";
import { getType } from "../../../util/mediaUtil";
import { OrganizeContext } from "../organize-context";

import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import Typography from "@mui/material/Typography";

import Empty from "../common/Empty";
import PanelLoadingIndicator from "../../../components/PanelLoadingIndicator";

const useStyles = makeStyles(theme => ({
  badge: {
    background: "rgba(125, 218, 252, 0.5)",
    borderRadius: 4,
    padding: "8px 12px",
    gap: 10,
    width: "auto",
    height: 32,
    marginRight: "30px",
  },

  content: {
    opacity: 0.5,
  },

  mediaType: {
    fontWeight: 700,
    fontSize: 14,
    lineHeight: "16px",
    color: theme.palette.primary.main,
  },
}));

const doSearch = (regex, list) =>
  filter(
    item =>
      test(regex, item.name) || test(regex, item.title) || test(regex, item.id),
    list,
  );

const ChildrenList = props => {
  const {
    addBreadcrumb,
    breadcrumbs,
    handleGroupCheckboxClick,
    query,
    selectedGroup,
  } = props;
  const classes = useStyles();
  const context = useContext(OrganizeContext);

  const parent = last(breadcrumbs);
  const useChildren = childrenHooks[parent.mediaType];
  const childrenResults = useChildren(parent.id, {
    limit: "all",
    sort: "auditUpdated desc",
  });
  const [children, setChildren] = useState([]);
  useEffect(() => {
    if (childrenResults.isSuccess) {
      const data = equals(parent.mediaType, "anthology")
        ? childrenResults.data.contents
        : childrenResults.data;
      const children = sort(byRecentlyUpdated, data);

      const regex = new RegExp(query, "ig");
      const searchResult = notBlank(query)
        ? doSearch(regex, children)
        : children;
      setChildren(searchResult);
    }
  }, [childrenResults.isSuccess, childrenResults.data, parent, query]);

  if (childrenResults.isSuccess && isBlank(children)) {
    return (
      <Empty
        message={
          notBlank(query) ? `No results for ${query}` : "This group is empty"
        }
      />
    );
  }

  return (
    <List dense disablePadding>
      {childrenResults.isLoading && <PanelLoadingIndicator />}
      {notBlank(children) &&
        children.map(child => (
          <ListItem
            button={!equals(child.mediaType, "content")}
            disabled={context.moveNotAllowed({ group: child })}
            classes={{
              root: clsx({
                [classes.content]: equals(child.mediaType, "content"),
              }),
            }}
            key={child.id}
            onClick={handleGroupCheckboxClick(child)}
          >
            {!equals(child.mediaType, "content") && (
              <ListItemIcon>
                <Checkbox
                  disabled={context.moveNotAllowed({ group: child })}
                  checked={equals(selectedGroup.id, child.id)}
                  color="primary"
                />
              </ListItemIcon>
            )}
            <ListItemText>{child.name}</ListItemText>
            <div className={classes.badge}>
              <Typography className={classes.mediaType}>
                {toUpper(getType(child))}
              </Typography>
            </div>

            {!equals(child.mediaType, "content") && (
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  onClick={addBreadcrumb(child)}
                  size="large"
                >
                  <FontAwesomeIcon icon={faAngleRight} />
                </IconButton>
              </ListItemSecondaryAction>
            )}
          </ListItem>
        ))}
    </List>
  );
};

export default ChildrenList;
